import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useStore } from './lib/store';
import Layout from './components/Layout';
import Login from './components/Login';
import Dashboard from './pages/Dashboard';
import Pipeline from './pages/Pipeline';
import Leads from './pages/Leads';
import Clients from './pages/Clients';
import Alerts from './pages/Alerts';
import PromoCodes from './pages/PromoCodes';
import Orders from './pages/Orders';
import Chains from './pages/Chains';
import JobQueue from './pages/JobQueue';
import { useEffect, useCallback } from 'react';
import { startTokenRefresh, stopTokenRefresh } from './lib/auth-refresh';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <Layout>{children}</Layout>;
}

function App() {
  const { isAuthenticated, fetchLeads, fetchClients, fetchAlerts, logout } = useStore();
  
  // Token auto-refresh management
  useEffect(() => {
    if (isAuthenticated) {
      // Démarrer le monitoring du token
      startTokenRefresh();
    } else {
      // Arrêter le monitoring si déconnecté
      stopTokenRefresh();
    }
    
    return () => {
      stopTokenRefresh();
    };
  }, [isAuthenticated]);
  
  // Écouter l'événement d'expiration du token
  useEffect(() => {
    const handleAuthExpired = () => {
      console.log('[App] Token expired, logging out');
      logout();
    };
    
    window.addEventListener('auth:expired', handleAuthExpired);
    
    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
    };
  }, [logout]);
  
  // Initial data fetch
  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads().then(() => {
        fetchAlerts(7);
      });
      fetchClients();
    }
  }, [isAuthenticated]);
  
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        } />
        <Route path="/" element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        } />
        <Route path="/clients" element={
          <PrivateRoute>
            <Clients />
          </PrivateRoute>
        } />
        <Route path="/pipeline" element={
          <PrivateRoute>
            <Pipeline />
          </PrivateRoute>
        } />
        <Route path="/leads" element={
          <PrivateRoute>
            <Leads />
          </PrivateRoute>
        } />
        <Route path="/alerts" element={
          <PrivateRoute>
            <Alerts />
          </PrivateRoute>
        } />
        <Route path="/promos" element={
          <PrivateRoute>
            <PromoCodes />
          </PrivateRoute>
        } />
        <Route path="/orders" element={
          <PrivateRoute>
            <Orders />
          </PrivateRoute>
        } />
        <Route path="/chains" element={
          <PrivateRoute>
            <Chains />
          </PrivateRoute>
        } />
        <Route path="/queue" element={
          <PrivateRoute>
            <JobQueue />
          </PrivateRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
